import React, { useEffect, useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight, 
  ArrowDownRight,
  BarChart3,
  Activity,
  DollarSign,
  Percent,
  Volume2,
  Clock,
  Filter,
  Search,
  RefreshCw,
  Star,
  AlertTriangle
} from 'lucide-react';
import { useAppStore } from '../store';
import { FuturesData } from '../services/marketData';
import { cn } from '../utils';
import { formatNumber } from '../utils';

const Markets: React.FC = () => {
  const { futuresData, updateFuturesData } = useAppStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('changePercent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Categorize futures
  const categories = {
    all: 'All Markets',
    currencies: 'Currencies',
    indices: 'Stock Indices',
    commodities: 'Commodities',
    energy: 'Energy'
  };

  const getCategoryForSymbol = (symbol: string): string => {
    if (['DXY', 'EUR', 'GBP', 'CHF', 'AUD', 'NZD', 'CAD', 'JPY'].includes(symbol)) return 'currencies';
    if (['SP500', 'NASDAQ', 'DOW'].includes(symbol)) return 'indices';
    if (['GOLD', 'SILVER', 'COPPER', 'PLATINUM'].includes(symbol)) return 'commodities';
    if (['CRUDE', 'GASOLINE', 'NATGAS'].includes(symbol)) return 'energy';
    return 'all';
  };

  // Filter and sort data
  const filteredData = futuresData
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.symbol.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || getCategoryForSymbol(item.symbol) === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const aValue = a[sortBy as keyof FuturesData] as number;
      const bValue = b[sortBy as keyof FuturesData] as number;
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });

  // Get market statistics
  const marketStats = {
    totalInstruments: futuresData.length,
    gainers: futuresData.filter(item => item.changePercent > 0).length,
    losers: futuresData.filter(item => item.changePercent < 0).length,
    unchanged: futuresData.filter(item => item.changePercent === 0).length,
    avgChange: futuresData.reduce((sum, item) => sum + item.changePercent, 0) / futuresData.length,
    totalVolume: futuresData.reduce((sum, item) => sum + (item.volume || 0), 0)
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const marketDataService = (await import('../services/marketData')).default;
      const freshData = await marketDataService.getFuturesData();
      updateFuturesData(freshData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-400';
    if (change < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'Bullish': return 'text-green-400 bg-green-500/20';
      case 'Bearish': return 'text-red-400 bg-red-500/20';
      default: return 'text-yellow-400 bg-yellow-500/20';
    }
  };

  const formatPrice = (symbol: string, price: number) => {
    if (symbol.includes('JPY')) return price.toFixed(2);
    if (symbol.includes('DXY')) return price.toFixed(2);
    if (price < 10) return price.toFixed(4);
    return formatNumber.currency(price);
  };

  useEffect(() => {
    // Auto-refresh every 30 seconds - REAL TIME DATA
    const interval = setInterval(async () => {
      try {
        const marketDataService = (await import('../services/marketData')).default;
        const freshData = await marketDataService.getFuturesData();
        updateFuturesData(freshData);
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Failed to auto-refresh data:', error);
      }
    }, 30000); // 30 seconds interval for real-time updates

    return () => clearInterval(interval);
  }, [updateFuturesData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Markets Overview</h1>
          <p className="text-gray-400">Real-time futures and indices data with comprehensive market analysis</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              "flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all duration-200",
              isRefreshing && "opacity-50 cursor-not-allowed"
            )}
          >
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
            Refresh
          </button>
          
          <div className="text-sm text-gray-400">
            Last update: {lastUpdate.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Market Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-400">Total</span>
          </div>
          <p className="text-xl font-bold text-white">{marketStats.totalInstruments}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-sm text-gray-400">Gainers</span>
          </div>
          <p className="text-xl font-bold text-green-400">{marketStats.gainers}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <span className="text-sm text-gray-400">Losers</span>
          </div>
          <p className="text-xl font-bold text-red-400">{marketStats.losers}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-gray-400">Unchanged</span>
          </div>
          <p className="text-xl font-bold text-yellow-400">{marketStats.unchanged}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Percent className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-gray-400">Avg Change</span>
          </div>
          <p className={cn("text-xl font-bold", getChangeColor(marketStats.avgChange))}>
            {marketStats.avgChange > 0 ? '+' : ''}{marketStats.avgChange.toFixed(2)}%
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="w-4 h-4 text-orange-400" />
            <span className="text-sm text-gray-400">Volume</span>
          </div>
          <p className="text-xl font-bold text-orange-400">
            {formatNumber.compact(marketStats.totalVolume)}
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="glass-card p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search instruments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {Object.entries(categories).map(([key, label]) => (
              <option key={key} value={key} className="bg-slate-800">{label}</option>
            ))}
          </select>
          
          {/* Sort Options */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="changePercent" className="bg-slate-800">Sort by Change %</option>
            <option value="price" className="bg-slate-800">Sort by Price</option>
            <option value="volume" className="bg-slate-800">Sort by Volume</option>
            <option value="name" className="bg-slate-800">Sort by Name</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-colors"
          >
            {sortOrder === 'desc' ? '↓' : '↑'}
          </button>
        </div>
      </div>

      {/* Markets Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Instrument</th>
                <th className="text-right p-4 text-gray-400 font-medium">Price</th>
                <th className="text-right p-4 text-gray-400 font-medium">Change</th>
                <th className="text-right p-4 text-gray-400 font-medium">Change %</th>
                <th className="text-right p-4 text-gray-400 font-medium">Volume</th>
                <th className="text-center p-4 text-gray-400 font-medium">Sentiment</th>
                <th className="text-center p-4 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => {
                const isPositive = item.changePercent > 0;
                const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
                
                return (
                  <tr key={item.symbol} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm",
                          isPositive ? "bg-green-500/20" : "bg-red-500/20"
                        )}>
                          {item.symbol.substring(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{item.name}</p>
                          <p className="text-sm text-gray-400">{item.symbol}</p>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-right">
                      <p className="font-medium text-white">
                        {formatPrice(item.symbol, item.price)}
                      </p>
                    </td>
                    
                    <td className="p-4 text-right">
                      <div className={cn("flex items-center justify-end gap-1", getChangeColor(item.change))}>
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">
                          {item.change > 0 ? '+' : ''}{item.change.toFixed(4)}
                        </span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-right">
                      <span className={cn("font-medium", getChangeColor(item.changePercent))}>
                        {item.changePercent > 0 ? '+' : ''}{formatNumber.percentage(item.changePercent)}
                      </span>
                    </td>
                    
                    <td className="p-4 text-right">
                      <span className="text-gray-300">
                        {item.volume ? formatNumber.compact(item.volume) : 'N/A'}
                      </span>
                    </td>
                    
                    <td className="p-4 text-center">
                      <span className={cn(
                        "px-2 py-1 rounded text-xs font-medium",
                        getSentimentColor(item.sentiment)
                      )}>
                        {item.sentiment}
                      </span>
                    </td>
                    
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button className="p-1 hover:bg-white/10 rounded transition-colors">
                          <Star className="w-4 h-4 text-gray-400 hover:text-yellow-400" />
                        </button>
                        <button className="p-1 hover:bg-white/10 rounded transition-colors">
                          <AlertTriangle className="w-4 h-4 text-gray-400 hover:text-orange-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No instruments found</p>
            <p className="text-gray-500 text-sm">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Markets;